import { avatarColors, initials, type Quality } from '@calling/shared';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { CloseIcon } from './icons';

export function Avatar({ name, id, size = 44 }: { name: string; id?: string; size?: number }) {
  const { avatar } = avatarColors(id ?? name);
  return (
    <span className="avatar" style={{ width: size, height: size, background: avatar, fontSize: Math.round(size * 0.36) }} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

const QUALITY_LABEL: Record<Quality, string> = { good: 'Good', fair: 'Fair', poor: 'Poor' };

export function SignalBars({ quality, label }: { quality: Quality; label?: boolean }) {
  return (
    <>
      <span className="bars" data-quality={quality} role="img" aria-label={`Connection quality: ${QUALITY_LABEL[quality]}`}>
        <i />
        <i />
        <i />
      </span>
      {label ? QUALITY_LABEL[quality] : null}
    </>
  );
}

export function Banner({ tone = 'warning', title, children, action, testId }: { tone?: 'warning' | 'danger' | 'info'; title: string; children?: ReactNode; action?: ReactNode; testId?: string }) {
  return (
    <div className={`banner${tone === 'warning' ? '' : ` banner--${tone}`}`} role={tone === 'info' ? 'status' : 'alert'} data-testid={testId}>
      <div className="stack stack--tight">
        <strong>{title}</strong>
        {children ? <span>{children}</span> : null}
      </div>
      {action}
    </div>
  );
}

/** The dialogs open now, the newest last: Android's back button closes the newest first (lib/backButton.ts). */
const openDialogs: Array<{ close: () => void }> = [];

/** Closes the newest open dialog, as Escape would. False when none is open. */
export function closeTopDialog(): boolean {
  const top = openDialogs.at(-1);
  top?.close();
  return top !== undefined;
}

/**
 * A modal dialog (a bottom sheet on phones). Closes on Escape, on a click
 * outside and on Android's back button, traps nothing behind it, and returns
 * focus to where it came from.
 */
export function Dialog({ title, onClose, children, labelledBy, testId }: { title?: string; onClose: () => void; children: ReactNode; labelledBy?: string; testId?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const latestClose = useRef(onClose);
  latestClose.current = onClose;
  useEffect(() => {
    const entry = { close: () => latestClose.current() };
    openDialogs.push(entry);
    return () => {
      const at = openDialogs.indexOf(entry);
      if (at >= 0) openDialogs.splice(at, 1);
    };
  }, []);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('[autofocus], input, select, textarea, button');
    (first ?? ref.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
    // Focus is managed once, when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      className="overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={labelledBy ?? (title ? headingId : undefined)} tabIndex={-1} ref={ref} data-testid={testId}>
        {title ? (
          <div className="row row--between">
            <h2 id={headingId}>{title}</h2>
            <button className="icon-btn icon-btn--plain" aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

export function Confirm({
  title,
  body,
  confirmLabel,
  danger,
  onConfirm,
  onCancel,
  testId,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  testId?: string;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog title={title} onClose={onCancel} testId={testId}>
      <p>{body}</p>
      <div className="dialog__actions">
        <button className="btn btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          className={`btn ${danger ? 'btn--danger' : 'btn--primary'}`}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
            } finally {
              setBusy(false);
            }
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

export interface ToastItem {
  id: number;
  text: string;
  tone: 'info' | 'warning' | 'danger';
  /** A button in the toast, such as Undo. Using it closes the toast. */
  action?: { label: string; onClick: () => void };
}

export function Toasts({ items, onDismiss }: { items: ToastItem[]; onDismiss?: (id: number) => void }) {
  return (
    <div className="toasts" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className="toast" data-tone={t.tone} role={t.tone === 'info' ? 'status' : 'alert'} onClick={() => onDismiss?.(t.id)}>
          {t.text}
          {t.action ? (
            <button
              className="toast__action"
              onClick={(e) => {
                e.stopPropagation();
                t.action!.onClick();
                onDismiss?.(t.id);
              }}
            >
              {t.action.label}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

let toastId = 1;

/** Short-lived messages for screens outside a call ("Link copied"). */
export function useToasts(durationMs = 2000): { toasts: ToastItem[]; show: (text: string, tone?: ToastItem['tone']) => void; dismiss: (id: number) => void } {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const dismiss = (id: number) => setToasts((list) => list.filter((t) => t.id !== id));
  const show = (text: string, tone: ToastItem['tone'] = 'info') => {
    const id = toastId++;
    setToasts((list) => [...list.slice(-2), { id, text, tone }]);
    window.setTimeout(() => dismiss(id), tone === 'info' ? durationMs : 6000);
  };
  return { toasts, show, dismiss };
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="screen screen--center" style={{ justifyContent: 'center' }} role="status">
      <div className="spinner" />
      <span className="muted">{label}</span>
    </div>
  );
}
